import {
    Box,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    List,
    ListItem,
    ListItemSecondaryAction,
    ListItemText,
    Switch,
    TextField,
    Typography,
    alpha,
    useTheme,
    InputAdornment
} from "@mui/material";
import { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { LuSearch, LuX } from "react-icons/lu";
import { Company } from "../../db_access/company";
import { TemplateSharing } from "../../db_access/template_sharing";
import { useContextProvider } from "../../components/page-context/ContextProvider";

const SharePortal = ({ open, onClose, templates = [], type, onSuccess }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { enqueueAlert } = useContextProvider();
    const [searchText, setSearchText] = useState("");
    const [companies, setCompanies] = useState([]);

    // sharedCompanyIds: Snapshot of what is believed to be shared (currently starts empty)
    const [sharedCompanyIds, setSharedCompanyIds] = useState(new Set());

    // tempSelected: Current UI state of toggles
    const [tempSelected, setTempSelected] = useState(new Set());

    const [loading, setLoading] = useState(false);

    // Fetch all companies on open and reset state
    useEffect(() => {
        if (open) {
            setLoading(true);
            Company.get_all()
                .then((res) => res.json())
                .then((data) => {
                    setCompanies(data || []);
                    setLoading(false);
                })
                .catch(() => setLoading(false));

            // TODO: Ideally we should fetch the list of companies this template is ALREADY shared with.
            // For now, we initialize as empty.
            setSharedCompanyIds(new Set());
            setTempSelected(new Set());
        }
    }, [open, templates]);

    const filteredCompanies = useMemo(() => {
        return companies.filter(c =>
            c.name.toLowerCase().includes(searchText.toLowerCase())
        );
    }, [companies, searchText]);

    const handleToggle = (companyId) => {
        const newSet = new Set(tempSelected);
        if (newSet.has(companyId)) {
            newSet.delete(companyId);
        } else {
            newSet.add(companyId);
        }
        setTempSelected(newSet);
    };

    const handleSave = async () => {
        setLoading(true);
        try {
            const toAdd = [...tempSelected].filter(id => !sharedCompanyIds.has(id));
            const toRemove = [...sharedCompanyIds].filter(id => !tempSelected.has(id));

            // Handle both single and multiple templates (ensure it's an array)
            const templateList = Array.isArray(templates) ? templates : [templates];

            const createPayload = (companyIds, targetTemplates) => {
                const payload = [];
                companyIds.forEach(companyId => {
                    targetTemplates.forEach(tpl => {
                        const templateId = tpl.product_id || tpl.project_id || tpl.building_id || tpl.structure_id || tpl.supply_chain_id || tpl.id;
                        payload.push({
                            company_id: companyId,
                            product_id: tpl.product_id || (type === 'product' ? templateId : null),
                            project_id: tpl.project_id || (type === 'project' ? templateId : null),
                            building_id: tpl.building_id || (type === 'building' ? templateId : null),
                            structure_id: tpl.structure_id || (type === 'structure' ? templateId : null),
                            supply_chain_id: tpl.supply_chain_id || (type === 'supply_chain' ? templateId : null),
                        });
                    });
                });
                return payload;
            };

            console.log('DEBUG: SharePortal Bulk Saving...', {
                type,
                count: templateList.length,
                toAdd,
                toRemove
            });

            const promises = [];
            if (toAdd.length > 0) {
                const addPayload = createPayload(toAdd, templateList);
                console.log('DEBUG: Bulk Payload to Add:', addPayload);
                promises.push(TemplateSharing.new(addPayload));
            }
            if (toRemove.length > 0) {
                const removePayload = createPayload(toRemove, templateList);
                console.log('DEBUG: Bulk Payload to Remove:', removePayload);
                promises.push(TemplateSharing.delete(removePayload));
            }

            const rawResults = await Promise.all(promises);
            console.log('DEBUG: SharePortal Full Results:', rawResults);

            // Attempt to parse results if they are fetch Response objects
            const parsedResults = await Promise.all(
                rawResults.map(async r => {
                    if (r instanceof Response) {
                        try { return await r.json(); } catch (e) { return r; }
                    }
                    return r;
                })
            );
            console.log('DEBUG: SharePortal Parsed Results:', parsedResults);

            enqueueAlert({ message: t("Permissions updated successfully"), severity: 'success' });
            if (onSuccess) onSuccess();
            onClose();
        } catch (error) {
            console.error('DEBUG: SharePortal Error:', error);
            enqueueAlert({ message: t("Failed to update permissions"), severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '12px',
                    backgroundImage: 'none',
                    boxShadow: '0 20px 40px rgba(0,0,0,0.1)'
                }
            }}
        >
            <DialogTitle sx={{ p: 0 }}>
                <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    p: 2.5,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    position: 'relative',
                    pl: 4, // Space for the accent bar
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: 12,
                        top: '25%',
                        bottom: '25%',
                        width: '4px',
                        borderRadius: '2px',
                        bgcolor: '#659d33' // Brand Green
                    }
                }}>
                    <Box sx={{ textAlign: 'left' }}>
                        <Typography sx={{ 
                            fontFamily: 'Poppins', 
                            fontSize: '18px', 
                            fontWeight: 700, 
                            color: theme.palette.text.primary,
                            lineHeight: 1.2
                        }}>
                            {t("Share Template")}
                        </Typography>
                        <Typography sx={{ 
                            fontSize: '12px', 
                            color: theme.palette.text.secondary,
                            fontWeight: 500,
                            mt: 0.5
                        }}>
                            {templates?.length === 1 ? templates[0]?.name : `${templates?.length} templates selected`}
                        </Typography>
                    </Box>
                    <IconButton onClick={onClose} size="small" sx={{ color: theme.palette.text.secondary }}>
                        <LuX size={20} />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent sx={{ p: 0, height: 500, display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ p: 2, bgcolor: alpha(theme.palette.background.default, 0.5), borderBottom: `1px solid ${theme.palette.divider}` }}>
                    <TextField
                        fullWidth
                        placeholder={t("Search organizations...")}
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        size="small"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <LuSearch color={theme.palette.text.secondary} />
                                </InputAdornment>
                            ),
                            sx: { borderRadius: 2, bgcolor: theme.palette.background.paper }
                        }}
                    />
                </Box>

                <List sx={{ flex: 1, overflowY: 'auto', px: 1 }}>
                    {filteredCompanies.map((company) => {
                        const isSelected = tempSelected.has(company.id);
                        return (
                            <ListItem
                                key={company.id}
                                sx={{
                                    borderRadius: 2,
                                    mb: 0.5,
                                    '&:hover': { bgcolor: theme.palette.action.hover }
                                }}
                            >
                                <ListItemText
                                    primary={company.name}
                                    secondary={company.email || company.city}
                                    primaryTypographyProps={{ fontWeight: 500 }}
                                />
                                <ListItemSecondaryAction>
                                    <Switch
                                        edge="end"
                                        checked={isSelected}
                                        onChange={() => handleToggle(company.id)}
                                        color="primary"
                                    />
                                </ListItemSecondaryAction>
                            </ListItem>
                        );
                    })}
                </List>
            </DialogContent>

            <Box sx={{ p: 2, borderTop: `1px solid ${theme.palette.divider}`, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                <Button onClick={onClose} variant="outlined" color="inherit" sx={{ borderRadius: 2 }}>
                    {t("Cancel")}
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    disabled={loading}
                    sx={{ borderRadius: 2, px: 3 }}
                >
                    {loading ? t("Saving...") : t("Share")}
                </Button>
            </Box>
        </Dialog>
    );
};

export default SharePortal;
